/**
 * Editor de un elemento del Learning Bank.
 *
 * Validación mínima y honesta: `kind` es obligatorio, `area` puede quedar
 * vacía, y no se puede guardar un elemento sin nada escrito. Nada más — un
 * formulario que exige rellenar seis campos después de una llamada de trabajo
 * no se usa.
 */

import { useState } from 'react';

import { AREAS, NOMBRE_AREA, type AreaOpcional } from '../../domain/modelo/areas';
import { KINDS, NOMBRE_KIND, type ElementoBanco, type Kind } from '../../domain/modelo/banco';
import { NOMBRE_PRIORIDAD, PRIORIDADES, type Prioridad } from '../../domain/modelo/prioridad';
import { bancoRepo } from '../../persistencia/bancoRepo';
import estilos from './EditorElemento.module.css';

interface Props {
  elemento: ElementoBanco;
  onCerrar: () => void;
}

export function EditorElemento({ elemento, onCerrar }: Props) {
  const [contexto, setContexto] = useState(elemento.contextoOriginal);
  const [target, setTarget] = useState(elemento.correccionTarget);
  const [notas, setNotas] = useState(elemento.notas ?? '');
  const [kind, setKind] = useState<Kind>(elemento.kind);
  const [area, setArea] = useState<AreaOpcional>(elemento.area);
  const [prioridad, setPrioridad] = useState<Prioridad>(elemento.prioridad);
  const [guardando, setGuardando] = useState(false);

  // No se guarda un elemento completamente vacío: sin contexto ni target no
  // hay nada que recuperar después.
  const vacio = !contexto.trim() && !target.trim();

  async function guardar() {
    if (vacio) return;
    setGuardando(true);
    try {
      await bancoRepo.guardar({
        ...elemento,
        contextoOriginal: contexto.trim(),
        correccionTarget: target.trim(),
        notas: notas.trim(),
        kind,
        area,
        prioridad,
      });
      onCerrar();
    } finally {
      setGuardando(false);
    }
  }

  return (
    <div className={estilos.fondo} role="dialog" aria-modal="true" aria-label="Editar elemento">
      <div className={estilos.panel}>
        <h2 className={estilos.titulo}>Editar</h2>

        <label className={estilos.campo}>
          <span className={estilos.etiqueta}>Contexto o situación</span>
          <textarea
            className={estilos.texto}
            rows={3}
            value={contexto}
            onChange={(e) => setContexto(e.target.value)}
          />
        </label>

        <label className={estilos.campo}>
          <span className={estilos.etiqueta}>Cómo se dice</span>
          <textarea
            className={estilos.texto}
            rows={2}
            value={target}
            onChange={(e) => setTarget(e.target.value)}
          />
        </label>

        <label className={estilos.campo}>
          <span className={estilos.etiqueta}>Notas</span>
          <textarea
            className={estilos.texto}
            rows={2}
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
          />
        </label>

        <label className={estilos.campo}>
          <span className={estilos.etiqueta}>Tipo</span>
          <select
            className={estilos.select}
            value={kind}
            onChange={(e) => setKind(e.target.value as Kind)}
          >
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {NOMBRE_KIND[k]}
              </option>
            ))}
          </select>
        </label>

        <label className={estilos.campo}>
          <span className={estilos.etiqueta}>Área</span>
          <select
            className={estilos.select}
            value={area ?? ''}
            onChange={(e) => setArea(e.target.value === '' ? null : (e.target.value as never))}
          >
            <option value="">Sin clasificar</option>
            {AREAS.map((a) => (
              <option key={a} value={a}>
                {NOMBRE_AREA[a]}
              </option>
            ))}
          </select>
        </label>

        <fieldset className={estilos.campo}>
          <legend className={estilos.etiqueta}>Prioridad</legend>
          <div className={estilos.prioridades}>
            {PRIORIDADES.map((p) => (
              <label
                key={p}
                className={
                  prioridad === p ? `${estilos.prioridad} ${estilos.prioridadActiva}` : estilos.prioridad
                }
              >
                <input
                  type="radio"
                  name="prioridad-editor"
                  value={p}
                  checked={prioridad === p}
                  onChange={() => setPrioridad(p)}
                  className="sr-only"
                />
                {NOMBRE_PRIORIDAD[p]}
              </label>
            ))}
          </div>
        </fieldset>

        {vacio ? (
          <p className={estilos.aviso} role="status">
            Escribí al menos el contexto o cómo se dice.
          </p>
        ) : null}

        <div className={estilos.acciones}>
          <button type="button" className={estilos.secundario} onClick={onCerrar}>
            Cancelar
          </button>
          <button
            type="button"
            className={estilos.principal}
            onClick={() => void guardar()}
            disabled={vacio || guardando}
          >
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
}
